import "./look.css";
import { store, type Theme } from "./state";

/**
 * The look switcher: live mini-previews of the two looks (Pink Sticker and
 * purple Neumorphic) and a circular reveal from the finger when you pick
 * one. Used two ways: as a bottom sheet from the gear on the countdown
 * screen (openLookSheet) and inline under Settings > Appearance
 * (mountLookPicker).
 */

export const THEMES: { id: Theme; label: string; blurb: string }[] = [
  { id: "sticker", label: "Sticker", blurb: "Hot pink, hard shadows" },
  { id: "chrome", label: "Neumorphic", blurb: "Purple chrome, soft light" },
];

/** Mirrors the tokens in sticker.css / style.css / chrome.css so each preview shows its look even while another is active. */
const PALETTES: Record<string, Record<string, string>> = {
  pink: { bg: "#ff5ca8", ink: "#1f0612", accent: "#e0006f", onaccent: "#fff0f7", lime: "#ff8fc6", sky: "#ffb8dc", surface: "#ffe4f1", shadow: "#1f0612" },
  chrome: { ink: "#34215c", page: "linear-gradient(160deg, #ffc9f6, #c9d6ff 30%, #d6c2ff 65%, #c2fff0)", grad: "linear-gradient(120deg, #ff5fd4, #a34dff)", hero: "linear-gradient(150deg, #ffd6fb, #d9bfff 45%, #bfe0ff)", num: "linear-gradient(180deg, #fff 10%, #a34dff 55%, #ff5fd4)", blob: "#ff8ce8, #c94dff", tint: "120, 60, 200" },
};

function paletteStyle(key: string): string {
  return Object.entries(PALETTES[key])
    .map(([k, v]) => `--m-${k}:${v}`)
    .join(";");
}

function daysLeft(): number {
  const event = store.getPrimaryEvent();
  if (!event) return 43;
  return Math.max(0, Math.floor((new Date(event.targetDate).getTime() - Date.now()) / 86_400_000));
}

/** A small live render of `theme`. */
function miniPreview(theme: Theme, days: number): string {
  const n = days.toLocaleString("en-GB");
  if (theme === "chrome") {
    return `<div class="mini mini--chrome" data-mini-variant="chrome" style="${paletteStyle("chrome")}" aria-hidden="true">
      <div class="mini-blob"></div>
      <div class="mini-glass mini-glass--ticket"><i></i><b></b></div>
      <div class="mini-chr-hero"><b>${n}</b><span></span></div>
      <div class="mini-chr-chips"><i class="mini-glass"></i><i class="mini-glass"></i></div>
      <div class="mini-chr-dial"><i></i><i class="on"></i><i></i></div>
    </div>`;
  }
  return `<div class="mini mini--${theme}" style="${paletteStyle("pink")}" aria-hidden="true">
    <div class="mini-ticket"><i></i><b></b></div>
    <div class="mini-hero"><b>${n}</b></div>
    <div class="mini-chips"><i></i><i></i></div>
    <div class="mini-card"><i></i><i></i></div>
  </div>`;
}

function pickerHTML(): string {
  const state = store.get();
  const days = daysLeft();
  return `
    <div class="look-carousel" role="radiogroup" aria-label="Style">
      ${THEMES.map(
        (t) => `
        <button type="button" class="look-card ${state.theme === t.id ? "is-active" : ""}" role="radio" aria-checked="${state.theme === t.id}" data-look-theme="${t.id}">
          ${miniPreview(t.id, days)}
          <span class="look-card-label">${t.label}</span>
          <span class="look-card-blurb">${t.blurb}</span>
          <span class="look-card-badge" aria-hidden="true">On</span>
        </button>`
      ).join("")}
    </div>`;
}

// ---------- applying a change ----------

function haptic() {
  try {
    navigator.vibrate?.(12);
  } catch {
    // iOS Safari has no Vibration API; the press animation carries it
  }
}

/**
 * Runs `update` inside a View Transition that reveals the new look as a
 * circle growing from (x, y). Falls back to an instant swap where View
 * Transitions aren't supported or the user prefers reduced motion.
 */
export function withReveal(update: () => void, x?: number, y?: number) {
  const doc = document as Document & { startViewTransition?: (cb: () => void) => { ready: Promise<void> } };
  const reduce = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  if (!doc.startViewTransition || reduce) {
    update();
    return;
  }
  const cx = x ?? window.innerWidth / 2;
  const cy = y ?? window.innerHeight / 2;
  const r = Math.hypot(Math.max(cx, window.innerWidth - cx), Math.max(cy, window.innerHeight - cy));
  const transition = doc.startViewTransition(update);
  transition.ready
    .then(() => {
      document.documentElement.animate(
        { clipPath: [`circle(0px at ${cx}px ${cy}px)`, `circle(${r}px at ${cx}px ${cy}px)`] },
        { duration: 560, easing: "cubic-bezier(.2,.8,.2,1)", pseudoElement: "::view-transition-new(root)" }
      );
    })
    .catch(() => {});
}

function pointFrom(e: Event, el: HTMLElement): [number, number] {
  const me = e as MouseEvent;
  if (me.clientX || me.clientY) return [me.clientX, me.clientY];
  const r = el.getBoundingClientRect();
  return [r.left + r.width / 2, r.top + r.height / 2];
}

/**
 * Wires a container holding pickerHTML(). Re-renders its contents on every
 * store change so previews and badges stay in sync with changes made
 * anywhere else.
 */
export function mountLookPicker(container: HTMLElement): () => void {
  container.classList.add("look-picker");
  let lastTheme = store.get().theme;
  let first = true;
  const paint = () => {
    const scrollLeft = container.querySelector<HTMLElement>(".look-carousel")?.scrollLeft ?? 0;
    const focused = document.activeElement as HTMLElement | null;
    const focusKey =
      focused && container.contains(focused) && focused.dataset.lookTheme ? `[data-look-theme="${focused.dataset.lookTheme}"]` : null;
    container.innerHTML = pickerHTML();
    const scroller = container.querySelector<HTMLElement>(".look-carousel")!;
    scroller.scrollLeft = scrollLeft;
    if (focusKey) container.querySelector<HTMLElement>(focusKey)?.focus({ preventScroll: true });
    const theme = store.get().theme;
    if (first) {
      // Open with the current style centred, without scrolling the page.
      first = false;
      const card = scroller.querySelector<HTMLElement>(".is-active");
      if (card) scroller.scrollLeft = card.offsetLeft - (scroller.clientWidth - card.offsetWidth) / 2;
    } else if (theme !== lastTheme) {
      lastTheme = theme;
      scroller.querySelector<HTMLElement>(".is-active")?.scrollIntoView({ behavior: "smooth", inline: "center", block: "nearest" });
    }
  };
  paint();

  const onClick = (e: Event) => {
    const target = e.target as HTMLElement;
    const themeBtn = target.closest<HTMLElement>("[data-look-theme]");
    if (!themeBtn) return;
    const id = themeBtn.dataset.lookTheme as Theme;
    if (id === store.get().theme) return;
    haptic();
    const [x, y] = pointFrom(e, themeBtn);
    withReveal(() => store.setLook({ theme: id }), x, y);
  };
  container.addEventListener("click", onClick);
  const unsubscribe = store.subscribe(paint);

  return () => {
    container.removeEventListener("click", onClick);
    unsubscribe();
  };
}

// ---------- the bottom sheet ----------

let openSheet: { close: () => void } | null = null;

/** Opens the look switcher as a bottom sheet. Drag it down, tap outside or press Escape to close. */
export function openLookSheet(returnFocus?: HTMLElement): void {
  if (openSheet) return;
  const host = document.getElementById("app")?.parentElement ?? document.body;
  const wrap = document.createElement("div");
  wrap.className = "look-sheet-wrap";
  wrap.innerHTML = `
    <div class="look-backdrop" data-el="backdrop"></div>
    <div class="look-sheet" role="dialog" aria-modal="true" aria-labelledby="look-title" tabindex="-1">
      <div class="look-grabber" data-el="grab"><span></span></div>
      <div class="look-sheet-head" data-el="head">
        <div class="look-title" id="look-title">Pick a look</div>
        <button type="button" class="look-done" data-el="done">Done</button>
      </div>
      <div data-el="picker"></div>
      <a class="look-more" href="#/settings" data-el="more"><span>All settings</span><span aria-hidden="true">→</span></a>
    </div>`;
  host.appendChild(wrap);

  const sheet = wrap.querySelector<HTMLElement>(".look-sheet")!;
  const q = (name: string) => wrap.querySelector<HTMLElement>(`[data-el="${name}"]`)!;
  const unmountPicker = mountLookPicker(q("picker"));

  requestAnimationFrame(() => wrap.classList.add("is-open"));
  sheet.focus({ preventScroll: true });

  let closed = false;
  function close() {
    if (closed) return;
    closed = true;
    wrap.classList.remove("is-open");
    sheet.style.transform = "";
    document.removeEventListener("keydown", onKey);
    window.removeEventListener("hashchange", closeNow);
    unmountPicker();
    const remove = () => wrap.remove();
    sheet.addEventListener("transitionend", remove, { once: true });
    window.setTimeout(remove, 400);
    openSheet = null;
    if (returnFocus?.isConnected) returnFocus.focus({ preventScroll: true });
  }

  // Leaving for another page: drop the sheet at once so it slides off with
  // the old page (router.ts) instead of lingering over the new one.
  function closeNow() {
    close();
    wrap.remove();
  }

  function onKey(e: KeyboardEvent) {
    if (e.key === "Escape") close();
    if (e.key === "Tab") {
      // Keep focus inside the sheet while it's open.
      const items = [...sheet.querySelectorAll<HTMLElement>("button:not([disabled]), a[href]")];
      const first = items[0];
      const last = items[items.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    }
  }
  document.addEventListener("keydown", onKey);
  window.addEventListener("hashchange", closeNow);
  q("backdrop").addEventListener("click", close);
  q("done").addEventListener("click", close);

  // Drag-to-dismiss from the grabber or the header, like a native sheet.
  let startY = 0;
  let dy = 0;
  let dragging = false;
  const onDown = (e: PointerEvent) => {
    if ((e.target as HTMLElement).closest("button")) return;
    dragging = true;
    startY = e.clientY;
    dy = 0;
    sheet.classList.add("is-dragging");
    (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
  };
  const onMove = (e: PointerEvent) => {
    if (!dragging) return;
    dy = Math.max(0, e.clientY - startY);
    sheet.style.transform = `translateY(${dy}px)`;
  };
  const onUp = () => {
    if (!dragging) return;
    dragging = false;
    sheet.classList.remove("is-dragging");
    if (dy > 90) close();
    else sheet.style.transform = "";
  };
  for (const handle of [q("grab"), q("head")]) {
    handle.addEventListener("pointerdown", onDown);
    handle.addEventListener("pointermove", onMove);
    handle.addEventListener("pointerup", onUp);
    handle.addEventListener("pointercancel", onUp);
  }

  openSheet = { close };
}

export function closeLookSheet() {
  openSheet?.close();
}
